import {ellipsis} from 'polished';
import React, {FC, useCallback} from 'react';
import styled from 'styled-components';

import {DataEventActionsEnum} from '../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {useDataEventTracking} from '../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useAppDispatch} from '../../../../../helpers/appReduxHelpers';
import {TopicTypeModel} from '../../../../../models/api/integrationModel';
import {TopicModel} from '../../../../../models/api/topicModel';
import {topicDataEventTracker} from '../../../../../services/tracking/topic';
import {removeTopicFromConversationsAction} from '../../../../../store/data/conversations/topicConversationsActions';
import {IntegrationIcon, IntegrationIconsEnum} from '../../../../common/integrationIcon';
import {IntegrationTopicIcon} from '../../../../common/integrationTopicIcon';
import {TopicDropdownContainer} from '../../conversation/header/topic/topicDropdownContainer';

/*
 * Props.
 */

interface ActivityTopicPillProps {
  conversationId: number;
  topic: TopicModel;
  topicType?: TopicTypeModel;
  shouldRemoveLeftMargin?: boolean;
}

/*
 * Style.
 */

interface PillStyleProps {
  $hasHover?: boolean;
  shouldRemoveLeftMargin?: boolean;
}

const StyledPillDiv = styled.div<PillStyleProps>`
  display: inline-block;

  vertical-align: middle;
  max-width: 200px;
  padding: 1px 3px;
  margin-left: ${(p) => (p.shouldRemoveLeftMargin ? 0 : '2px')};

  background: ${(p) => p.theme.greys.shade10};
  background: ${(p) => (p.$hasHover ? p.theme.greys.shade30 : p.theme.greys.shade10)};
  border: 1px solid ${(p) => p.theme.greys.shade60};
  border-radius: 2px;

  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.veryTiny};
  font-weight: ${fontWeights.semibold};
  text-transform: uppercase;

  &:hover {
    background: ${(p) => p.theme.greys.shade30};
  }

  ${ellipsis('200px')};
`;

const StyledTopicIntegrationWrapper = styled.div`
  width: 18px;
  height: 18px;
  display: inline-block;
  margin: -3px -1px -5px -3px;
`;

const StyledIntegrationWrapper = styled.div`
  display: inline-block;
  margin: 0 -3px -10px 0;
`;

const StyledIntegrationIcon = styled(IntegrationIcon)`
  margin: 0;
  padding: 0;
`;

const StyledIntegrationTopicIcon = styled(IntegrationTopicIcon)`
  margin: 0;
  padding: 0;
`;

const StyledPopoverCoordinatorWrapperDiv = styled.div`
  display: inline-block;
  margin: 0;
`;

/*
 * Component.
 */

export const ActivityTopicPill: FC<ActivityTopicPillProps> = (props) => {
  const {conversationId, topic} = props;
  const dispatch = useAppDispatch();
  const {trackDataEvent} = useDataEventTracking();

  const trackAction = useCallback(
    (action: DataEventActionsEnum) => {
      trackDataEvent(...topicDataEventTracker(action, topic));
    },
    [trackDataEvent, topic],
  );

  const renderPopover = (popoverProps: PopoverTransferProps) => {
    const removeTopic = () => {
      dispatch(removeTopicFromConversationsAction([conversationId], topic.id));
    };

    return (
      <TopicDropdownContainer
        {...props}
        {...popoverProps}
        topic={topic}
        removeTopic={removeTopic}
        onOpenUrl={() => trackAction(DataEventActionsEnum.OPEN_URL)}
        shouldShowViewDetails
      />
    );
  };

  return (
    <StyledPopoverCoordinatorWrapperDiv>
      <PopoverCoordinator
        renderButton={(isOpen) => renderTopicButton(isOpen, props)}
        renderModal={renderPopover}
        onPopoverToggle={(isVisible) => {
          if (!isVisible) {
            return;
          }

          trackAction(DataEventActionsEnum.CLICK);
        }}
      />
    </StyledPopoverCoordinatorWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderTopicButton(isOpen: boolean, props: ActivityTopicPillProps) {
  const {topic, topicType, shouldRemoveLeftMargin} = props;

  const hasHover = isOpen && Boolean(topic);

  return (
    <StyledPillDiv $hasHover={hasHover} shouldRemoveLeftMargin={shouldRemoveLeftMargin}>
      <TooltipCoordinator render={() => topic.name}>
        <TooltipOverflowCondition>
          {renderIntegrationIcon(topicType)}
          {topic.name}
        </TooltipOverflowCondition>
      </TooltipCoordinator>
    </StyledPillDiv>
  );
}

function renderIntegrationIcon(topicType?: TopicTypeModel) {
  if (topicType) {
    return (
      <StyledTopicIntegrationWrapper>
        <StyledIntegrationTopicIcon integrationTopicIconUrl={topicType.image} />
      </StyledTopicIntegrationWrapper>
    );
  }

  return (
    <StyledIntegrationWrapper>
      <StyledIntegrationIcon integrationIcon={IntegrationIconsEnum.WEB_TOPIC} />
    </StyledIntegrationWrapper>
  );
}
